
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/RichTextEditor";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Section {
  id: number;
  order: number;
  prompt: string;
  date: string | null;
  description: string;
  title: string;
  includeInReport: boolean;
  visible: boolean;
  canClone: boolean;
  key: string;
  temperature: number;
  preset: string | null;
  model: string;
  canDelete: boolean;
  checked: boolean;
  editable: boolean;
}

interface AddSectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (section: Omit<Section, 'id' | 'order'>) => void;
}

export const AddSectionDialog = ({ open, onOpenChange, onSave }: AddSectionDialogProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prompt: ''
  });

  const handleSave = () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }
    
    const newSection: Omit<Section, 'id' | 'order'> = {
      title: formData.title,
      description: formData.description,
      prompt: formData.prompt,
      checked: true,
      editable: true,
      canDelete: true,
      date: null,
      includeInReport: true,
      visible: true,
      canClone: true,
      key: formData.title.toLowerCase().replace(/\s+/g, '_'),
      temperature: 0.7,
      preset: null,
      model: 'gpt-4o-mini'
    };
    
    onSave(newSection);
    setFormData({
      title: '',
      description: '',
      prompt: ''
    });
    onOpenChange(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[15000] bg-black/50" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[15001] grid w-full max-w-[600px] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg max-h-[80vh] overflow-y-auto"
          )}
        >
          <div className="flex flex-col space-y-1.5 text-center sm:text-left">
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              {t('add_new_section')}
            </DialogPrimitive.Title>
          </div>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="title">{t('title')}</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={t('section_title')}
              />
            </div>
            
            <div>
              <Label htmlFor="description">{t('description')}</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('section_description')}
              />
            </div>
            
            <div>
              <Label htmlFor="prompt">{t('prompt')}</Label>
              <RichTextEditor
                value={formData.prompt}
                onChange={(value) => handleInputChange('prompt', value)}
                placeholder={t('write_prompt_section')}
                className="mt-2"
              />
            </div>
          </div>
          
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
              {t('cancel')}
            </Button>
            <Button onClick={handleSave} disabled={!formData.title.trim() || !formData.description.trim()} className="w-full sm:w-auto">
              {t('add')}
            </Button>
          </div>

          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
